import elliemaeApi from "./interceptors/elliemaeApi";

/**
 * Fetches REO properties from Encompass API
 * @param {string} token
 * @param {string} loanid
 * @param {string} applicationId
 * @returns {Promise<Array>} List of REO properties
 */
export const fetchREOs = async (token, loanid, applicationId) => {
    try {
        const response = await elliemaeApi.get(
            `/encompass/v3/loans/${loanid}/applications/${applicationId}/reoProperties`,
            {
                headers: { Authorization: `Bearer ${token}` }
            }
        );
        return response.data;
    } catch (error) {
        console.error("Failed to fetch REOs:", error.message);
        return [];
    }
};

/**
 * Fetches all VOL (liabilities) records from Encompass API
 * @param {string} token
 * @param {string} loanid
 * @param {string} applicationId
 * @returns {Promise<Array>} List of VOLs
 */
export const fetchVOLs = async (token, loanid, applicationId) => {
    try {
        const response = await elliemaeApi.get(
            `/encompass/v3/loans/${loanid}/applications/${applicationId}/vols`,
            {
                headers: { Authorization: `Bearer ${token}` }
            }
        );
        return response.data;
    } catch (error) {
        console.error("Failed to fetch VOLs:", error.message);
        return [];
    }
};

const readOnlyKeys = [
    'altId',
    'noLinkToDocTrackIndicator',
    'sourceOfIncomeData',
    'lienHolderIds',
    'populatedContactIndicator',
    'lienHolder'
];

const sanitizeREO = (reo) => {
    const cleaned = { ...reo };
    readOnlyKeys.forEach(key => delete cleaned[key]);
    return cleaned;
};


export const submitREOData = async (reoList, assignedVolsMap, token, loanid, applicationId) => {
    const addList = reoList
        .filter(item => !item.id && item.streetAddress)
        .map(sanitizeREO);

    const updateList = reoList
        .filter(item => item.id)
        .map(sanitizeREO);
    const baseURL = `/encompass/v3/loans/${loanid}/applications/${applicationId}/reoProperties`;

    const patchConfig = (action, data) => ({
        method: 'patch',
        url: `${baseURL}?action=${action}&view=entity`,
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        data: JSON.stringify(data)
    });

    try {
        if (addList.length > 0) {
            await elliemaeApi.request(patchConfig('add', addList));
        }

        if (updateList.length > 0) {
            await elliemaeApi.request(patchConfig('update', updateList));
        }


        const currentVolAssignments = new Set();

        const volAssignments = [];

        for (const reo of reoList) {
            if (reo.id && Array.isArray(reo.lienHolderIds)) {
                for (const volId of reo.lienHolderIds) {
                    currentVolAssignments.add(volId); // Keep track of current links

                    // Always (re)link assigned
                    volAssignments.push({
                        id: volId,
                        reoProperty: {
                            entityId: reo.id,
                            entityType: "ReoProperty"
                        }
                    });
                }
            }
        }

        // Detect removed ones: present in assignedMap but NOT in currentVolAssignments
        for (const volId in assignedVolsMap) {
            if (!currentVolAssignments.has(volId)) {
                volAssignments.push({
                    id: volId,
                    reoProperty: null
                });
            }
        }

        console.log('volAssignments');
        console.log(volAssignments);

        if (volAssignments.length > 0) {
            await elliemaeApi.patch(
                `/encompass/v3/loans/${loanid}/applications/${applicationId}/vols?action=update&view=entity`,
                volAssignments,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
        }

        console.log("REO and VOL linking submitted successfully.");
    } catch (error) {
        console.error('REO patch failed:', error.response?.data || error.message);
    }
};

/**
 * Submit new VOD assets (only new rows) to Encompass API.
 * Then refresh VODs and update the list.
 * 
 * @param {Array} vodList Current asset list
 * @param {Function} setVodList React state setter
 * @param {string} token Encompass auth token
 * @param {string} loanid Loan ID
 * @param {string} applicationId App ID
 */
export const submitVODData = async (vodList, setVodList, token, loanid, applicationId) => {
    const newAssets = vodList
        .filter(v =>
            v.isNew &&
            v.holderName &&
            v.items?.[0]?.accountIdentifier &&
            v.items?.[0]?.urla2020CashOrMarketValueAmount
        )
        .map(v => ({
            holderName: v.holderName,
            items: [
                {
                    itemNumber: 1,
                    accountIdentifier: v.items[0].accountIdentifier,
                    urla2020CashOrMarketValueAmount: v.items[0].urla2020CashOrMarketValueAmount
                }
            ]
        }));


    if (newAssets.length === 0) return;

    try {
        // Submit new VODs
        await elliemaeApi.patch(
            `/encompass/v3/loans/${loanid}/applications/${applicationId}/vods?action=add&view=entity`,
            newAssets,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            }
        );

        // Reload from API after add
        const refreshed = await fetchVODs(token, loanid, applicationId);

        const mapped = refreshed.map(vod => ({
            holderName: vod.holderName || '',
            items: vod.items || [{ itemNumber: 1, accountIdentifier: '', urla2020CashOrMarketValueAmount: '' }],
            isExisting: true,
            isNew: false
        }));

        // Pad to 6
        while (mapped.length < 6) {
            mapped.push({
                holderName: '',
                items: [{ itemNumber: 1, accountIdentifier: '', urla2020CashOrMarketValueAmount: '' }],
                isExisting: false,
                isNew: false
            });
        }

        setVodList(mapped.slice(0, 6));
        console.log("VOD submission and refresh complete.");
    } catch (error) {
        console.error("VOD submission failed:", error.response?.data || error.message);
    }
};

export const fetchVODs = async (token, loanid, applicationId) => {
    try {
        const response = await elliemaeApi.get(
            `/encompass/v3/loans/${loanid}/applications/${applicationId}/vods`,
            {
                headers: { Authorization: `Bearer ${token}` }
            }
        );
        return response.data;
    } catch (error) {
        console.error("Failed to fetch VODs:", error.message);
        return [];
    }
};


export async function updateVods(token, loanId, applicationId, vodUpdateArray) {
    if (!token) throw new Error("Missing token");
    if (!loanId) throw new Error("Missing loanId");
    if (!applicationId) throw new Error("Missing applicationId");

    const url = `/encompass/v3/loans/${loanId}/applications/${applicationId}/vods?action=update`;

    const res = await elliemaeApi.patch(url, vodUpdateArray, {
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
        }
    });

    return res.data;
}

export const fetchGiftsGrants = async (token, loanId, applicationId) => {
    try {
        const response = await elliemaeApi.get(
            `/encompass/v3/loans/${loanId}/applications/${applicationId}/giftsGrants`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            }
        );

        return response.data;
    } catch (error) {
        console.error("Failed to fetch Gifts/Grants:", error.message);
        return [];
    }
};

export const fetchOtherAssets = async (token, loanId, applicationId) => {
    try {
        const response = await elliemaeApi.get(
            `/encompass/v3/loans/${loanId}/applications/${applicationId}/otherAssets`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            }
        );

        return response.data;
    } catch (error) {
        console.error("Failed to fetch Other Assets:", error.message);
        return [];
    }
};
